import type { CSSProperties } from "react";
import {
  RETAIL_QUOTE_CANADIAN_OWNERSHIP_BODY,
  RETAIL_QUOTE_CANADIAN_OWNERSHIP_HEADING,
  RETAIL_QUOTE_CREDIT_CARD_CHARGE_NOTE
} from "../lib/retail-quote-copy";
import {
  RETAIL_QUOTE_INERT_ORDER_URL,
  normalizeRetailQuoteOrderCta,
  retailQuoteOrderCtaSafetyLabel,
  retailQuoteOrderDisplayUrl,
  retailQuoteOrderQrSvg,
  type RetailQuoteOrderCta
} from "../lib/retail-quote-qr";

export const RETAIL_QUOTE_SHEET_STYLESHEET_PATH = "/assets/retail-quotes/v3/sell-sheet.css";
export const RETAIL_QUOTE_DOCUMENT_WIDTH_IN = 8.5;
export const RETAIL_QUOTE_DOCUMENT_HEIGHT_IN = 14.6;
export const RETAIL_QUOTE_DENSE_DOCUMENT_HEIGHT_IN = 16.5;
export const RETAIL_QUOTE_DOCUMENT_INSET_IN = 0.25;
export const RETAIL_QUOTE_DOCUMENT_INNER_WIDTH_IN = RETAIL_QUOTE_DOCUMENT_WIDTH_IN - (2 * RETAIL_QUOTE_DOCUMENT_INSET_IN);
export const RETAIL_QUOTE_DOCUMENT_INNER_HEIGHT_IN = RETAIL_QUOTE_DOCUMENT_HEIGHT_IN - (2 * RETAIL_QUOTE_DOCUMENT_INSET_IN);
export const RETAIL_QUOTE_DENSE_DOCUMENT_INNER_HEIGHT_IN = RETAIL_QUOTE_DENSE_DOCUMENT_HEIGHT_IN - (2 * RETAIL_QUOTE_DOCUMENT_INSET_IN);

const RETAIL_QUOTE_CAN_ASSETS: Record<string, string> = {
  "RP-12-GC": "ginger-citrus",
  "RP-12-KL": "key-lime",
  "RP-12-BR": "blue-raspberry",
  "RP-12-BO": "blood-orange",
  "RP-12-PL": "pink-lemonade",
  "RP-12-PM": "peach-mango"
};

const RETAIL_QUOTE_ORIGINAL_SKUS = [
  "RP-12-GC",
  "RP-12-KL",
  "RP-12-BR",
  "RP-12-BO",
  "RP-12-PL",
  "RP-12-PM"
];

const RETAIL_QUOTE_MIX_RECIPES: Record<string, string[]> = {
  "RP-MIX-TRI": ["RP-12-BR", "RP-12-GC", "RP-12-KL"],
  "RP-MIX-NEW3": ["RP-12-PM", "RP-12-BO", "RP-12-PL"],
  "RP-MIX-ALL6": RETAIL_QUOTE_ORIGINAL_SKUS
};

const RETAIL_QUOTE_FALLBACK_CLAIMS: RetailQuoteSheetClaim[] = [
  { id: "caffeine", text: "Each 355 mL can contains 100 mg of caffeine from green tea." },
  { id: "formula", text: "Ranch Power includes Lion's Mane, L-Theanine, electrolytes, and vitamins B6 and B12." },
  { id: "sugar", text: "Ranch Power has no added sugar." },
  { id: "sweeteners", text: "Ranch Power has no artificial sweeteners." }
];

export interface RetailQuoteSheetClaim {
  id?: string;
  text?: string;
}

export interface RetailQuoteSheetProduct {
  id?: string;
  skuId?: string;
  skuCode?: string;
  upc?: string;
  name?: string;
  productName?: string;
  pack?: string;
  suggestedRetailPrice?: number;
  wholesaleCaseCost?: number;
  visualColor?: string;
  visualLabel?: string;
}

export interface RetailQuoteSheetPricing {
  cansPerCase?: number;
  containerFeeNote?: string;
  costPerCan?: number;
  grossMarginPercent?: number;
  grossProfitPerCan?: number;
  issueDate?: string;
  lines?: RetailQuoteSheetProduct[];
  msrp?: number;
  quotedCaseCost?: number;
  terms?: string;
}

export interface RetailQuoteSheetFulfillmentOption {
  customerCopy?: string;
  providerName?: string;
}

export interface RetailQuoteSheetFulfillment {
  enabled?: boolean;
  options?: RetailQuoteSheetFulfillmentOption[];
  customerCopy?: string;
  providerName?: string;
}

export interface RetailQuoteSheetProps {
  claims?: RetailQuoteSheetClaim[];
  contactName?: string;
  documentMode?: boolean;
  fulfillment?: RetailQuoteSheetFulfillment | null;
  note?: string;
  orderCta?: RetailQuoteOrderCta | null;
  orderingAccessEnabled?: boolean;
  pricing?: RetailQuoteSheetPricing | null;
  products?: RetailQuoteSheetProduct[];
  repEmail?: string;
  repLocation?: string;
  repName?: string;
  repPhone?: string;
  retailerName?: string;
}

/** Canonical data contract used by both the authenticated preview and PDF HTML. */
export type RetailQuoteSheetData = RetailQuoteSheetProps;

function clean(value: unknown, limit = 240) {
  return String(value ?? "").replace(/[\r\n]+/g, " ").trim().slice(0, limit).trim();
}

function message(value: unknown, limit = 500) {
  return String(value ?? "").replace(/\r\n?/g, "\n").trim().slice(0, limit).trim();
}

export type RetailQuoteSheetDensityInput = Pick<
  RetailQuoteSheetProps,
  "claims" | "contactName" | "fulfillment" | "note" | "pricing" | "products" | "repLocation" | "retailerName"
>;

export function isRetailQuoteSheetDense(data: RetailQuoteSheetDensityInput) {
  const pricingLines = Array.isArray(data.pricing?.lines) ? data.pricing.lines : [];
  const productLines = Array.isArray(data.products) ? data.products : [];
  const lineCount = pricingLines.length || productLines.length;
  const deliveryCopyLength = fulfillmentOptions(data.fulfillment)
    .reduce((total, option) => total + clean(option.providerName).length + clean(option.customerCopy, 400).length, 0);
  const claimsLength = (Array.isArray(data.claims) ? data.claims : [])
    .slice(0, 4)
    .reduce((total, claim) => total + clean(claim.text, 500).length, 0);
  return lineCount > 3
    || message(data.note, 500).length > 280
    || deliveryCopyLength > 520
    || claimsLength > 520
    || (clean(data.retailerName, 240).length + clean(data.contactName, 240).length) > 120
    || clean(data.repLocation, 240).length > 120;
}

export function retailQuoteDocumentHeightIn(data: RetailQuoteSheetDensityInput) {
  return isRetailQuoteSheetDense(data) ? RETAIL_QUOTE_DENSE_DOCUMENT_HEIGHT_IN : RETAIL_QUOTE_DOCUMENT_HEIGHT_IN;
}

function money(value: unknown) {
  const number = Number(value);
  return Number.isFinite(number) ? `$${number.toFixed(2)}` : "-";
}

function percent(value: unknown) {
  const number = Number(value);
  return Number.isFinite(number) ? `${number.toFixed(1)}%` : "-";
}

function dateLabel(value: unknown) {
  const match = String(value ?? "").match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return clean(value) || "-";
  const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]), 12));
  return new Intl.DateTimeFormat("en-CA", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC"
  }).format(date);
}

function fulfillmentOptions(value?: RetailQuoteSheetFulfillment | null) {
  if (!value || value.enabled !== true) return [];
  if (Array.isArray(value.options)) return value.options;
  return [value];
}

function canPath(skuId: unknown) {
  const slug = RETAIL_QUOTE_CAN_ASSETS[clean(skuId, 120)];
  return slug ? `/assets/retail-quotes/v2/cans/${slug}.png` : "";
}

/** First, middle and last preserve the balanced hero used by the live preview. */
export function retailQuoteHeroSkus(lines: RetailQuoteSheetProduct[] | null | undefined) {
  const output: string[] = [];
  (Array.isArray(lines) ? lines : []).forEach((line) => {
    const skuId = clean(line && (line.id || line.skuId), 120);
    const candidates = RETAIL_QUOTE_CAN_ASSETS[skuId] ? [skuId] : (RETAIL_QUOTE_MIX_RECIPES[skuId] || []);
    candidates.forEach((candidate) => {
      if (!output.includes(candidate)) output.push(candidate);
    });
  });
  if (!output.length) output.push("RP-12-GC", "RP-12-BR", "RP-12-PM");
  if (output.length <= 3) return output;
  return [output[0], output[Math.floor(output.length / 2)], output[output.length - 1]];
}

function RetailQuoteLogo() {
  return (
    <div className="retail-quote-sheet-logo">
      <img
        className="retail-quote-sheet-logo-buffalo"
        src="/assets/brand/buffalo.svg"
        alt="Ranch Power"
      />
      <div className="retail-quote-sheet-logo-copy">
        <img
          className="retail-quote-sheet-logo-wordmark"
          src="/assets/brand/wordmark.svg"
          alt="Ranch Power"
        />
        <div className="retail-quote-sheet-logo-tagline">GRIT. DRIVEN.</div>
      </div>
    </div>
  );
}

export function RetailQuoteMetric({ accent, label, sub, value }: { accent?: string; label: string; sub?: string; value: string }) {
  return (
    <div className="retail-quote-metric" style={{ "--quote-metric-accent": accent || "var(--navy)" } as CSSProperties}>
      <div className="retail-quote-metric-label">{label}</div>
      <div className="retail-quote-metric-value tnum">{value}</div>
      {sub && <div className="retail-quote-metric-sub">{sub}</div>}
    </div>
  );
}

export function RetailQuoteProductBadge({ product }: { product: RetailQuoteSheetProduct }) {
  const color = product.visualColor || "var(--amber)";
  const label = product.visualLabel || clean(product.id || product.skuId, 120).slice(-3);
  return <span className="retail-quote-product-badge" style={{ background: color }}>{label}</span>;
}

function RetailQuoteCan({ alt = "", className = "", skuId }: { alt?: string; className?: string; key?: string; skuId: string }) {
  const src = canPath(skuId);
  if (!src) return null;
  return <img className={className} src={src} alt={alt} loading="eager" />;
}

function RetailQuoteProductVisual({ product }: { product: RetailQuoteSheetProduct }) {
  const skuId = clean(product && (product.id || product.skuId), 120);
  if (canPath(skuId)) {
    return (
      <div className="retail-quote-sheet-product-can">
        <RetailQuoteCan skuId={skuId} alt={`${clean(product.name || product.productName)} can`} />
      </div>
    );
  }
  const recipe = RETAIL_QUOTE_MIX_RECIPES[skuId] || [];
  if (recipe.length) {
    const previewRecipe = recipe.length > 3 ? [recipe[0], recipe[2], recipe[5]] : recipe;
    return (
      <div className="retail-quote-sheet-mix-cans" aria-label="Mixed pack assortment">
        {previewRecipe.map((recipeSku) => <RetailQuoteCan key={recipeSku} skuId={recipeSku} alt="" />)}
      </div>
    );
  }
  return <RetailQuoteProductBadge product={product} />;
}

function isMixedProduct(product: RetailQuoteSheetProduct) {
  return Boolean(RETAIL_QUOTE_MIX_RECIPES[clean(product && (product.id || product.skuId), 120)]);
}

function resolvedOrderCta(orderCta: RetailQuoteOrderCta | null | undefined, orderingAccessEnabled: boolean) {
  const candidate = orderCta || (orderingAccessEnabled
    ? { mode: "preview" as const, url: RETAIL_QUOTE_INERT_ORDER_URL }
    : null);
  return normalizeRetailQuoteOrderCta(candidate, "preview");
}

export function RetailQuoteSheet({
  claims,
  contactName,
  documentMode = false,
  fulfillment,
  note,
  orderCta,
  orderingAccessEnabled = false,
  pricing: pricingInput,
  products,
  repEmail,
  repLocation,
  repName,
  repPhone,
  retailerName
}: RetailQuoteSheetProps) {
  const pricing = pricingInput || {};
  const productRows = Array.isArray(products) ? products : [];
  const lines = Array.isArray(pricing.lines) && pricing.lines.length
    ? pricing.lines.map((line) => ({
        ...line,
        id: line.skuId || line.id,
        name: line.productName || line.name
      }))
    : productRows;
  const displayClaims = Array.isArray(claims) && claims.length ? claims : RETAIL_QUOTE_FALLBACK_CLAIMS;
  const personalMessage = message(note, 500);
  const cleanRepName = clean(repName, 160) || "Ranch Power Sales";
  const repFirstName = cleanRepName.split(/\s+/)[0] || "your rep";
  const heroSkus = retailQuoteHeroSkus(lines);
  const dense = isRetailQuoteSheetDense({
    claims,
    contactName,
    fulfillment,
    note,
    pricing,
    products: productRows,
    repLocation,
    retailerName
  });
  const productColumns = lines.length === 1 ? 1 : (lines.length === 2 || lines.length === 4 ? 2 : 3);
  const deliveryOptions = fulfillmentOptions(fulfillment);
  const normalizedOrderCta = resolvedOrderCta(orderCta, orderingAccessEnabled);
  const orderSafetyLabel = normalizedOrderCta ? retailQuoteOrderCtaSafetyLabel(normalizedOrderCta.mode) : "";
  const rootClassName = [
    "retail-quote-sheet",
    "retail-quote-print-root",
    dense ? "is-dense" : "",
    personalMessage ? "has-message" : "has-no-message",
    normalizedOrderCta ? "has-order-cta" : "",
    documentMode ? "is-document" : ""
  ].filter(Boolean).join(" ");

  return (
    <div className="retail-quote-sheet-frame">
    <article className={rootClassName} aria-label="Ranch Power retail partner proposal">
      <header className="retail-quote-sheet-hero">
        <div className="retail-quote-sheet-hero-copy">
          <RetailQuoteLogo />
          <span className="retail-quote-sheet-kicker">Retail partner proposal</span>
          <h2>Clean energy for<br />long days and hard miles.</h2>
          <p>Prepared exclusively for <strong>{clean(retailerName) || "your store"}</strong></p>
        </div>
        <div className={`retail-quote-sheet-hero-cans cans-${heroSkus.length}`} aria-hidden="true">
          {heroSkus.map((skuId, index) => <RetailQuoteCan key={skuId} skuId={skuId} className={`can-${index + 1}`} />)}
        </div>
      </header>

      <section className="retail-quote-sheet-recipient">
        <div>
          <span className="retail-quote-sheet-kicker">Your Ranch Power offer</span>
          <h3>{clean(retailerName) || "Your store"}</h3>
          <p>{clean(contactName) ? `For ${clean(contactName)}` : "For your retail team"}</p>
        </div>
        <div className="retail-quote-sheet-dates">
          <span>Issued <strong>{dateLabel(pricing.issueDate)}</strong></span>
        </div>
      </section>

      {personalMessage && (
        <section className="retail-quote-sheet-letter">
          <span className="retail-quote-sheet-letter-mark" aria-hidden="true">“</span>
          <div>
            <span className="retail-quote-sheet-letter-title">A quick note from {repFirstName}</span>
            <blockquote>{personalMessage}</blockquote>
            <p>{cleanRepName} <span>· Your Ranch Power rep</span></p>
          </div>
        </section>
      )}

      <section className="retail-quote-sheet-economics" aria-label="Retail economics">
        <RetailQuoteMetric label="Wholesale case" value={money(pricing.quotedCaseCost)} sub={`${pricing.cansPerCase || 12} cans`} accent="var(--amber)" />
        <RetailQuoteMetric label="Cost per can" value={money(pricing.costPerCan)} sub="before container fees" accent="var(--navy)" />
        <RetailQuoteMetric label="Suggested retail" value={money(pricing.msrp)} sub="per can" accent="var(--ok)" />
        <RetailQuoteMetric label="Gross margin" value={percent(pricing.grossMarginPercent)} sub={`${money(pricing.grossProfitPerCan)} profit per can`} accent="var(--info)" />
      </section>

      <section className="retail-quote-sheet-products">
        <div className="retail-quote-sheet-section-head">
          <div><span className="retail-quote-sheet-index">01</span><span>Your Ranch Power lineup</span></div>
          <small>355 mL - 12 cans per case</small>
        </div>
        <div className={`retail-quote-sheet-product-grid columns-${productColumns} products-${Math.min(lines.length, 9)}`}>
          {lines.map((product) => (
            <div className={`retail-quote-sheet-product${isMixedProduct(product) ? " is-mixed" : ""}`} key={product.id || product.skuId}>
              <RetailQuoteProductVisual product={product} />
              <span>
                <strong>{clean(product.name || product.productName).replace(" 355ml", "")}</strong>
                <small aria-label={`${product.pack || "12-Pack"}; SKU ${clean(product.skuCode, 120) || "Unavailable"}${clean(product.upc, 12) ? `; UPC ${clean(product.upc, 12)}` : ""}`}>
                  <span>{clean(product.pack, 80) || "12-Pack"} · SKU {clean(product.skuCode, 120) || "Unavailable"}</span>
                  {clean(product.upc, 12) && <span>· UPC {clean(product.upc, 12)}</span>}
                </small>
                <em>{money(product.wholesaleCaseCost)} case · {money(product.suggestedRetailPrice)} retail</em>
              </span>
            </div>
          ))}
        </div>
      </section>

      <section className="retail-quote-sheet-claims">
        <div className="retail-quote-sheet-section-head">
          <div><span className="retail-quote-sheet-index">02</span><span>Why Ranch Power</span></div>
        </div>
        <div className="retail-quote-sheet-claim-grid">
          {displayClaims.slice(0, 4).map((claim, index) => (
            <div key={claim.id || index}><span aria-hidden="true">{String(index + 1).padStart(2, "0")}</span><p>{claim.text}</p></div>
          ))}
        </div>
      </section>

      <section className="retail-quote-sheet-disclosure">
        <strong>Order details</strong>
        <span>{pricing.containerFeeNote} Terms: {pricing.terms || "As confirmed by Ranch Power"}. All amounts are in CAD.</span>
        <span><b>Payment note:</b> {RETAIL_QUOTE_CREDIT_CARD_CHARGE_NOTE}</span>
        {deliveryOptions.length > 0 && (
          <span><b>Ordering &amp; delivery option{deliveryOptions.length === 1 ? "" : "s"}:</b> {deliveryOptions.map((option) => `${clean(option.providerName)} - ${clean(option.customerCopy, 400)}`).join(" Or: ")}</span>
        )}
      </section>

      <section className="retail-quote-sheet-brand-close" aria-label="Ranch Power is proudly Canadian">
        <img src="/assets/brand/buffalo.svg" alt="" />
        <div>
          <strong>{RETAIL_QUOTE_CANADIAN_OWNERSHIP_HEADING}</strong>
          <p>{RETAIL_QUOTE_CANADIAN_OWNERSHIP_BODY}</p>
        </div>
      </section>

      <footer className="retail-quote-sheet-footer">
        <div className="retail-quote-sheet-footer-action">
          {orderSafetyLabel && <span className="retail-quote-sheet-order-safety">{orderSafetyLabel}</span>}
          <span>Ready to stock Ranch Power?</span>
          <strong>{normalizedOrderCta ? "Scan to sign in or request secure ordering access." : `Reply to ${repFirstName} to place an order.`}</strong>
          {normalizedOrderCta && <small>{retailQuoteOrderDisplayUrl(normalizedOrderCta.url)}</small>}
        </div>
        {normalizedOrderCta && (
          <div
            className="retail-quote-sheet-order-qr"
            dangerouslySetInnerHTML={{ __html: retailQuoteOrderQrSvg(normalizedOrderCta.url) }}
          />
        )}
        <div className="retail-quote-sheet-footer-contact">
          <strong>{cleanRepName}</strong>
          <span>{repEmail || "sales@ranchpower.com"}</span>
          {repPhone && <span>{repPhone}</span>}
          {repLocation && <span>{repLocation}</span>}
          <span>ranchpower.com</span>
        </div>
      </footer>
    </article>
    </div>
  );
}
